import { ComponentProps } from "react";
import { useWindowSize } from "@hooks/useWindowSize";
import Card from "@particles/card/Card";
import CardGroupedListItem from "@particles/card/components/CardGroupedList/CardGroupedListItem";
import { CardVariations } from "@particles/card/utils/types";
import { ImagePresets } from "@routes/Article/components/ArticleThumbnail/utils/enums";
import { getTopStoriesLinkWithRef } from "@routes/Home/Overview/utils/helpers";
import { CaaSImageFilters } from "@typings/OptimisedImage";
import cn from "classnames";
import { isEmpty } from "lodash-es";

export type HomepageTopStoriesProps = {
  /** Individual article */
  cardProps: ComponentProps<typeof Card>;
  /** Index of the article in the list */
  index: number;
  /** Grouped article props */
  groupedCardProps?: ComponentProps<typeof CardGroupedListItem>[];
};

/**
 * Renders a single story card for the Homepage Top Stories section.
 * https://www.figma.com/design/rmzJoZwUpb3iLm6lMDXJ2W/BT-MASTER--WEB-?node-id=1312-1855&t=g7P8QlYHW3Ymfii6-4
 */
const HomepageTopStories = ({
  cardProps,
  index,
  groupedCardProps,
}: HomepageTopStoriesProps): React.ReactElement => {
  const { isScreenLG } = useWindowSize();
  const isLeadArticle = index === 0;
  const isDisplayMediaArticle = [5, 6].includes(index);
  const displayMedia = isDisplayMediaArticle || (isScreenLG && isLeadArticle);
  const isDisplayGroupedArticle = isLeadArticle && !isEmpty(groupedCardProps);

  const leadCardFilters: CaaSImageFilters = {
    w: ImagePresets.LARGE,
    dpr: 1,
    f: "webp",
  };

  const otherCardFilters: CaaSImageFilters = {
    w: ImagePresets.MEDIUM,
    dpr: 1,
    f: "webp",
  };

  const variant: CardVariations = {
    image: {
      position: isDisplayMediaArticle ? "left" : "",
      width: cn({
        "w-3/10": isDisplayMediaArticle,
        "w-full": isLeadArticle,
      }),
      imageClassName: cn("object-cover", {
        "aspect-3x2": isLeadArticle && isDisplayGroupedArticle,
      }),
      extraClass: cn({
        "lg:h-[375px]": isLeadArticle && !isDisplayGroupedArticle,
      }),
      filters: isLeadArticle ? leadCardFilters : otherCardFilters,
      loading: "eager",
    },
    content: {
      width: cn({ "w-7/10": isDisplayMediaArticle, "w-full": isLeadArticle }),
      bgColor: cn({ "bg-gray-850": isLeadArticle }),
      updatedTimeColor: isLeadArticle ? "!text-white" : "",
      extraClass: cn({
        "text-center items-center w-full text-white lg:p-9 text-balance justify-center":
          isLeadArticle && !isDisplayGroupedArticle,
        "pl-4": isDisplayMediaArticle,
        "px-6 text-center lg:text-left":
          isLeadArticle && isDisplayGroupedArticle,
      }),
    },
    kicker: {
      color: "text-gray-515",
      extraClass: "uppercase",
    },
    title: {
      size: cn("text-base", { "text-4xl": isLeadArticle }),
      extraClass: "font-lct pt-1",
      color: cn("text-gray-850", { "text-white": isLeadArticle }),
    },
    subscriberLabel: {
      extraClass: cn("w-fit mx-auto lg:mx-0"),
    },
  };

  const _link = getTopStoriesLinkWithRef(cardProps.link, index, isScreenLG);

  return (
    <div
      className={cn("story border-gray-175 lg:order-1 lg:col-span-1", {
        "order-1 lg:row-span-4": isLeadArticle,
        "lg:mb-0": [0, 4, 8].includes(index),
        "lg:border-r lg:pr-3": index < 5,
        "order-2": index === 5,
        "order-3": index === 6,
        "order-4": (index >= 1 && index < 5) || index > 6,
        "lg:pl-3": index > 0,
      })}
      data-testid="homepage-top-stories-individual-component"
    >
      <Card
        {...cardProps}
        variations={variant}
        className={cn("h-full border-b border-gray-175 pb-3 pt-3", {
          "lg:border-b-0 lg:pb-0": [0, 4, 8].includes(index),
          "lg:pt-0": [0, 1, 5].includes(index),
          "flex-col !flex-nowrap": isLeadArticle,
        })}
        thumbnail={displayMedia ? cardProps.thumbnail : undefined}
        defaultImage={displayMedia ? cardProps.defaultImage : undefined}
        groupedArticles={isDisplayGroupedArticle ? groupedCardProps : undefined}
        link={_link}
      />
    </div>
  );
};

export default HomepageTopStories;
